import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import type { PluginBootstrapError } from './PluginBootstrapError';

interface PluginBootstrapErrorsProps {
  error: PluginBootstrapError;
}

export function PluginBootstrapErrors({ error }: PluginBootstrapErrorsProps) {
  const items: { title: string; names: string[]; testId: string }[] = [];
  if (error.unknownPlugins?.length) {
    items.push({
      title: 'Unknown plugins',
      names: error.unknownPlugins,
      testId: 'plugin-bootstrap-error-unknown',
    });
  }
  if (error.systemPluginsRequested?.length) {
    items.push({
      title: 'System plugins cannot be installed manually',
      names: error.systemPluginsRequested,
      testId: 'plugin-bootstrap-error-system',
    });
  }
  if (error.missingDependencies?.length) {
    items.push({
      title: 'Missing dependencies',
      names: error.missingDependencies,
      testId: 'plugin-bootstrap-error-missing',
    });
  }
  if (error.capabilityGatedRequested?.length) {
    items.push({
      title: 'Requires capabilities not granted to this organization',
      names: error.capabilityGatedRequested,
      testId: 'plugin-bootstrap-error-capability-gated',
    });
  }
  if (error.releaseChannelGatedRequested?.length) {
    items.push({
      title: 'Preview plugins cannot be installed during creation',
      names: error.releaseChannelGatedRequested,
      testId: 'plugin-bootstrap-error-release-channel-gated',
    });
  }
  if (items.length === 0) return null;
  return (
    <Alert variant="destructive" data-testid="plugin-bootstrap-error">
      <AlertTitle>Selection rejected by the server</AlertTitle>
      <AlertDescription>
        <ul className="mt-1 space-y-1 text-sm">
          {items.map((it) => (
            <li key={it.title} data-testid={it.testId}>
              <span className="font-medium">{it.title}:</span> {it.names.join(', ')}
            </li>
          ))}
        </ul>
        {error.capabilityGatedRequested?.length ? (
          <p
            data-testid="plugin-bootstrap-error-capability-hint"
            className="mt-2 text-xs text-muted-foreground"
          >
            A platform admin can grant the missing capability on the organization detail page.
          </p>
        ) : null}
        {error.releaseChannelGatedRequested?.length ? (
          <p
            data-testid="plugin-bootstrap-error-release-channel-hint"
            className="mt-2 text-xs text-muted-foreground"
          >
            An organization manager can enable the plugin's release channel and install it from the
            Plugins section of the Admin panel after setup.
          </p>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}
